import React, { useState } from 'react';
import styled from 'styled-components';
import { FaPlus, FaTrash, FaPen, FaRobot, FaSignOutAlt } from 'react-icons/fa';
import { chatTitleService } from '../services/chatTitle.service';
import UserPermissionManager from './UserPermissionManager';

const SidebarContainer = styled.div`
  width: 250px;
  height: 100vh;
  background-color: #003776;
  border-right: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
`;

const SidebarHeader = styled.div`
  padding: 20px;
  text-align: center;
  border-bottom: 1px solid #004a9f;
  font-weight: bold;
  font-size: 18px;
  color: #fff;
  background-color: #00285a;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const BrandName = styled.span`
  color: #ffffff;
  font-weight: 700;
  font-size: 22px;
`;

const SubtitleText = styled.span`
  color: #a0c0e0;
  font-size: 14px;
  font-weight: normal;
`;

const TabBar = styled.div`
  display: flex;
  border-bottom: 1px solid #004a9f;
`;

const Tab = styled.button`
  padding: 10px 15px;
  cursor: pointer;
  font-size: 14px;
  border: none;
  background: transparent;
  border-bottom: 2px solid ${props => props.active ? '#ffffff' : 'transparent'};
  color: ${props => props.active ? '#ffffff' : '#a0c0e0'};
  
  &:hover {
    color: #ffffff;
    background-color: #004a9f;
  }
`;

const NewChatButton = styled.button`
  margin: 15px;
  padding: 10px;
  border: none;
  border-radius: 4px;
  background-color: #10a37f;
  color: white;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #0d8c6d;
  }
`;

const LogoutButton = styled.button`
  margin: 15px;
  padding: 10px;
  border: none;
  border-radius: 4px;
  background-color: #004a9f;
  color: white;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #5a6268;
  }
`;

const ChatsList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  
  /* Customize scrollbar for dark theme */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #003776;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #0073e6;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #0066cc;
  }
`;

const ChatItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  margin-bottom: 5px;
  border-radius: 4px;
  cursor: pointer;
  background-color: ${props => props.active ? '#1a5ca0' : 'transparent'};
  color: ${props => props.active ? '#ffffff' : '#e0e0e0'};
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.active ? '#1a5ca0' : '#004a9f'};
    color: #ffffff;
  }
`;

const ChatTitle = styled.div`
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: inherit;
`;

const ChatDate = styled.div`
  font-size: 12px;
  color: #a0c0e0;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: #a0c0e0;
  cursor: pointer;
  padding: 4px;
  opacity: 0;
  transition: opacity 0.2s, color 0.2s;
  
  ${ChatItem}:hover & {
    opacity: 1;
  }
  
  &:hover {
    color: #ffffff;
  }
`;

const EmptySessionsMessage = styled.div`
  text-align: center;
  color: #a0c0e0;
  margin: 20px;
  padding: 20px;
  font-size: 14px;
  background-color: #004a9f;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
`;


const Sidebar = ({ 
  activeChat, 
  sessions, 
  onNewChat, 
  onSelectChat, 
  onDeleteChat, 
  activeTab, 
  onTabChange,
  onLogout
}) => {
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [titleInput, setTitleInput] = useState('');
  const [sessionTitles, setSessionTitles] = useState({});

  // Fetch custom title if not already loaded
  const fetchTitle = async (sessionId) => {
    if (sessionTitles[sessionId]) return;
    const res = await chatTitleService.getTitle(sessionId);
    if (res.success && res.title) {
      setSessionTitles(prev => ({ ...prev, [sessionId]: res.title }));
    }
  };
  // Format date as DD/MM/YYYY
  const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  // Always show full year and leading zeroes
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};
  
  // Get chat title: custom title if set, else first message, else default
  const getChatTitle = (session) => {
    if (sessionTitles[session.session_id]) return sessionTitles[session.session_id];
    if (session.first_message) {
      const maxLength = 30;
      return session.first_message.length > maxLength
        ? session.first_message.substring(0, maxLength) + '...'
        : session.first_message;
    }
    return 'New Chat';
  };

  // Start editing a session title
  const handleEditTitle = (session) => {
    setEditingSessionId(session.session_id);
    setTitleInput(sessionTitles[session.session_id] || getChatTitle(session));
  };

  // Save session title
  const handleSaveTitle = async (session) => {
    if (titleInput.trim()) {
      await chatTitleService.setTitle(session.session_id, titleInput.trim());
      setSessionTitles(prev => ({ ...prev, [session.session_id]: titleInput.trim() }));
    }
    setEditingSessionId(null);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingSessionId(null);
    setTitleInput('');
  };

  
  return (
    <SidebarContainer>
      <SidebarHeader>
        <FaRobot size={20} />
        <BrandName>Vcurd</BrandName>
        <SubtitleText>AI Governance</SubtitleText>
      </SidebarHeader>
      <NewChatButton onClick={onNewChat}>
        <FaPlus size={14} />
        New Chat
      </NewChatButton>
      
      <ChatsList>
        {sessions.length === 0 ? (
          <EmptySessionsMessage>
            No chat sessions yet. Start a new chat!
          </EmptySessionsMessage>
        ) : (
           sessions.map(session => (
             <ChatItem 
               key={session.session_id} 
               active={activeChat === session.session_id}
               onClick={() => {
                 onSelectChat(session.session_id);
                 fetchTitle(session.session_id);
               }}
             >
               <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                 {editingSessionId === session.session_id ? (
  <div style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between', gap: 8 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
      <input
        value={titleInput}
        onChange={e => setTitleInput(e.target.value)}
        onClick={e => e.stopPropagation()}
        onKeyDown={e => {
          if (e.key === 'Enter') handleSaveTitle(session);
          if (e.key === 'Escape') handleCancelEdit();
        }}
        autoFocus
        style={{
          fontSize: 14,
          padding: '4px 8px',
          borderRadius: 4,
          border: '1px solid #bdbdbd',
          width: 110,
          outline: 'none',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
        }}
        onFocus={e => e.target.style.border = '1.5px solid #0084ff'}
        onBlur={e => e.target.style.border = '1px solid #bdbdbd'}
      />
      <button
        onClick={e => { e.stopPropagation(); handleSaveTitle(session); }}
        style={{
          background: '#e6f2ff',
          border: 'none',
          borderRadius: 4,
          padding: '2px 6px',
          fontSize: 14,
          color: '#0084ff',
          cursor: 'pointer',
          transition: 'background 0.2s',
        }}
        title="Save"
        onMouseOver={e => e.currentTarget.style.background = '#cce6ff'}
        onMouseOut={e => e.currentTarget.style.background = '#e6f2ff'}
      >✔</button>
      <button
        onClick={e => { e.stopPropagation(); handleCancelEdit(); }}
        style={{
          background: '#f8d7da',
          border: 'none',
          borderRadius: 4,
          padding: '2px 6px',
          fontSize: 14,
          color: '#d32f2f',
          cursor: 'pointer',
          transition: 'background 0.2s',
        }}
        title="Cancel"
        onMouseOver={e => e.currentTarget.style.background = '#f5b7b1'}
        onMouseOut={e => e.currentTarget.style.background = '#f8d7da'}
      >✖</button>
    </div>
    <ChatDate style={{ marginLeft: 8, flexShrink: 0, whiteSpace: 'nowrap', minWidth: 70, maxWidth: 90, textAlign: 'right', overflow: 'hidden' }}>{formatDate(session.last_activity)}</ChatDate>
  </div>
) : (
                   <>
                     <ChatTitle>{getChatTitle(session)}</ChatTitle>
                     <FaPen
                       style={{ marginLeft: 4, fontSize: 11, cursor: 'pointer', color: '#888' }}
                       title="Rename chat"
                       onClick={e => { e.stopPropagation(); handleEditTitle(session); }}
                     />
                   </>
                 )}
                 <ChatDate>{formatDate(session.last_activity)}</ChatDate>
               </div>
               <ActionButton onClick={e => {
                 e.stopPropagation();
                 onDeleteChat(session.session_id);
               }}>
                 <FaTrash size={14} />
               </ActionButton>
             </ChatItem>
           ))
        )}
      </ChatsList>
      
      <LogoutButton onClick={onLogout}>
        <FaSignOutAlt size={14} />
        Logout
      </LogoutButton>
    </SidebarContainer>
  );
};

export default Sidebar;
